import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  FiUsers,
  FiPlus,
  FiSearch,
  FiGlobe,
  FiLock,
  FiCheckCircle,
  FiTrash2,
  FiLogOut,
  FiUserCheck,
  FiInfo,
  FiRefreshCw,
  FiUserX,
  FiX,
  FiShield,
} from "react-icons/fi";
import {
  getPublicCommunities,
  getMemberCommunities,
  getAdminCommunities,
  addCommunity,
  joinCommunity,
  leaveCommunity,
  deleteCommunity,
  getCommunityDetails,
  getCommunityMembers,
  removeUserFromCommunity,
} from "../../../api/customer/customerFeatureApis";

const getInitials = (firstName = "", lastName = "") => {
  const f = firstName ? firstName.charAt(0) : "";
  const l = lastName ? lastName.charAt(0) : "";
  return (f + l).toUpperCase() || "U";
};

export default function CustomerCommunitiesHub() {
  const authState = useSelector((state) => state.auth);
  const token = authState?.user?.token || authState?.token || authState?.user?.refreshToken;
  const currentUserId = authState?.user?.userDetails?.id || authState?.user?.id;

  const [activeTab, setActiveTab] = useState("public"); // "public" | "joined" | "managed"
  const [searchQuery, setSearchQuery] = useState("");

  const [publicCommunities, setPublicCommunities] = useState([]);
  const [joinedCommunities, setJoinedCommunities] = useState([]);
  const [managedCommunities, setManagedCommunities] = useState([]);

  const [loading, setLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [alertMsg, setAlertMsg] = useState(null);

  // Create Community Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCommunityName, setNewCommunityName] = useState("");
  const [newCommunityType, setNewCommunityType] = useState("PUBLIC");
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState("");

  // Details & Members Modal state
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [communityMembers, setCommunityMembers] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState(null);

  const fetchAllData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setAlertMsg(null);
    try {
      const [pubRes, joinRes, adminRes] = await Promise.all([
        getPublicCommunities(token, { page_number: 1, page_size: 100 }),
        getMemberCommunities(token, { page_number: 1, page_size: 100 }),
        getAdminCommunities(token, { page_number: 1, page_size: 100 }),
      ]);

      const mapItems = (list = [], role = null) =>
        list.map((c) => {
          const areaDesc = c.area
            ? `${c.area.name || ""}${c.area.city_name ? ", " + c.area.city_name : ""}`.trim()
            : c.name || "Neighborhood Community";
          return {
            id: c.id,
            name: c.name || "Untitled Community",
            description: areaDesc,
            type: c.type || "PUBLIC",
            created_on: c.created_on,
            area: c.area,
            role,
          };
        });

      setPublicCommunities(mapItems(pubRes?.communities || []));
      setJoinedCommunities(mapItems(joinRes?.communities || [], "MEMBER"));
      setManagedCommunities(mapItems(adminRes?.communities || [], "ADMIN"));
    } catch (err) {
      console.error("Failed to load communities:", err);
      setAlertMsg({ type: "danger", text: "Failed to load communities. Please refresh." });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Join community
  const handleJoin = async (communityId) => {
    setActionLoadingId(communityId);
    try {
      const res = await joinCommunity(token, communityId);
      if (res?.statusType === "SUCCESS" || res?.status === "SUCCESS") {
        setAlertMsg({ type: "success", text: res.text || "Successfully joined community!" });
        fetchAllData();
      } else {
        setAlertMsg({ type: "danger", text: res?.text || "Failed to join community." });
      }
    } catch (err) {
      setAlertMsg({
        type: "danger",
        text: err?.response?.data?.text || "Failed to join community.",
      });
    } finally {
      setActionLoadingId(null);
    }
  };

  // Leave community
  const handleLeave = async (communityId) => {
    if (!window.confirm("Are you sure you want to leave this community?")) return;
    setActionLoadingId(communityId);
    try {
      const res = await leaveCommunity(token, communityId);
      if (res?.statusType === "SUCCESS" || res?.status === "SUCCESS") {
        setAlertMsg({ type: "success", text: res.text || "You have left the community." });
        fetchAllData();
      } else {
        setAlertMsg({ type: "danger", text: res?.text || "Failed to leave community." });
      }
    } catch (err) {
      setAlertMsg({
        type: "danger",
        text: err?.response?.data?.text || "Failed to leave community.",
      });
    } finally {
      setActionLoadingId(null);
    }
  };

  // Delete community (Admin)
  const handleDelete = async (communityId) => {
    if (!window.confirm("Are you sure you want to permanently delete this community? This action cannot be undone.")) {
      return;
    }
    setActionLoadingId(communityId);
    try {
      const res = await deleteCommunity(token, communityId);
      if (res?.statusType === "SUCCESS" || res?.status === "SUCCESS") {
        setAlertMsg({ type: "success", text: res.text || "Community deleted successfully." });
        fetchAllData();
      } else {
        setAlertMsg({ type: "danger", text: res?.text || "Failed to delete community." });
      }
    } catch (err) {
      setAlertMsg({
        type: "danger",
        text: err?.response?.data?.text || "Failed to delete community.",
      });
    } finally {
      setActionLoadingId(null);
    }
  };

  // Create new community
  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!newCommunityName.trim()) {
      setCreateError("Please enter a community name.");
      return;
    }

    setCreateSubmitting(true);
    setCreateError("");
    try {
      const res = await addCommunity(token, {
        name: newCommunityName.trim(),
        type: newCommunityType,
      });

      if (res?.statusType === "SUCCESS" || res?.status === "SUCCESS") {
        setAlertMsg({ type: "success", text: res.text || "Community created successfully!" });
        setShowCreateModal(false);
        setNewCommunityName("");
        setNewCommunityType("PUBLIC");
        fetchAllData();
      } else {
        setCreateError(res?.text || "Failed to create community.");
      }
    } catch (err) {
      setCreateError(err?.response?.data?.text || "Failed to create community.");
    } finally {
      setCreateSubmitting(false);
    }
  };

  // View community details & members
  const handleOpenDetails = async (community) => {
    setSelectedCommunity(community);
    setShowDetailsModal(true);
    setDetailsLoading(true);
    setCommunityMembers([]);
    try {
      const [detailRes, membersRes] = await Promise.all([
        getCommunityDetails(token, community.id),
        getCommunityMembers(token, community.id),
      ]);

      if (detailRes?.statusType === "SUCCESS" && detailRes.community) {
        setSelectedCommunity((prev) => ({ ...prev, ...detailRes.community }));
      }
      if (membersRes?.statusType === "SUCCESS" && membersRes.communityMembers) {
        setCommunityMembers(membersRes.communityMembers);
      }
    } catch (err) {
      console.error("Failed to load community details:", err);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Remove user from community (Admin only)
  const handleRemoveMember = async (targetUserId) => {
    if (!window.confirm("Are you sure you want to remove this member from the community?")) return;
    setRemovingMemberId(targetUserId);
    try {
      const res = await removeUserFromCommunity(token, selectedCommunity.id, targetUserId);
      if (res?.statusType === "SUCCESS" || res?.status === "SUCCESS") {
        setCommunityMembers((prev) => prev.filter((m) => m?.user?.id !== targetUserId));
        setAlertMsg({ type: "success", text: "Member removed from community." });
      } else {
        setAlertMsg({ type: "danger", text: res?.text || "Failed to remove member." });
      }
    } catch (err) {
      setAlertMsg({ type: "danger", text: err?.response?.data?.text || "Failed to remove member." });
    } finally {
      setRemovingMemberId(null);
    }
  };

  // Filtered lists based on active tab & search query
  const displayedCommunities = useMemo(() => {
    let source = [];
    if (activeTab === "public") source = publicCommunities;
    else if (activeTab === "joined") source = joinedCommunities;
    else if (activeTab === "managed") source = managedCommunities;

    if (!searchQuery.trim()) return source;
    const q = searchQuery.toLowerCase();
    return source.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.description && c.description.toLowerCase().includes(q))
    );
  }, [activeTab, publicCommunities, joinedCommunities, managedCommunities, searchQuery]);

  const joinedIds = useMemo(
    () => new Set(joinedCommunities.map((c) => String(c.id))),
    [joinedCommunities]
  );
  const managedIds = useMemo(
    () => new Set(managedCommunities.map((c) => String(c.id))),
    [managedCommunities]
  );

  return (
    <div className="container-fluid px-3 px-md-4 py-3">
      {/* Page Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
        <div>
          <h3 className="fw-bold mb-1 d-flex align-items-center gap-2 text-dark">
            <FiUsers className="text-primary" /> Neighborhood Communities
          </h3>
          <p className="text-muted mb-0 small">
            Connect, collaborate, and discover verified local communities in your neighborhood.
          </p>
        </div>
        <div className="d-flex gap-2 w-100 w-md-auto">
          <button
            className="btn btn-outline-secondary d-flex align-items-center gap-1 btn-sm"
            onClick={fetchAllData}
            disabled={loading}
          >
            <FiRefreshCw className={loading ? "spin-icon" : ""} /> Refresh
          </button>
          <button
            className="btn btn-primary d-flex align-items-center gap-2 btn-sm px-3 shadow-sm"
            onClick={() => {
              setCreateError("");
              setNewCommunityName("");
              setNewCommunityType("PUBLIC");
              setShowCreateModal(true);
            }}
          >
            <FiPlus /> Create Community
          </button>
        </div>
      </div>

      {/* Global Alert Notification */}
      {alertMsg && (
        <div
          className={`alert alert-${alertMsg.type} alert-dismissible fade show d-flex align-items-center justify-content-between mb-3`}
          role="alert"
        >
          <span>{alertMsg.text}</span>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setAlertMsg(null)}
          />
        </div>
      )}

      {/* Summary Metrics Banner */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-md-4">
          <div
            className={`card border-0 shadow-sm p-3 rounded-3 cursor-pointer ${
              activeTab === "public" ? "border-start border-primary border-4" : ""
            }`}
            style={{ background: "#f8faff", transition: "all 0.2s" }}
            onClick={() => setActiveTab("public")}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <span className="text-muted small fw-medium">Discover Public</span>
                <h4 className="fw-bold text-primary mb-0 mt-1">{publicCommunities.length}</h4>
              </div>
              <div
                className="rounded-circle d-flex align-items-center justify-content-center bg-primary bg-opacity-10 text-primary"
                style={{ width: "44px", height: "44px" }}
              >
                <FiGlobe size={22} />
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-4">
          <div
            className={`card border-0 shadow-sm p-3 rounded-3 cursor-pointer ${
              activeTab === "joined" ? "border-start border-success border-4" : ""
            }`}
            style={{ background: "#f6fdf9", transition: "all 0.2s" }}
            onClick={() => setActiveTab("joined")}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <span className="text-muted small fw-medium">Joined Communities</span>
                <h4 className="fw-bold text-success mb-0 mt-1">{joinedCommunities.length}</h4>
              </div>
              <div
                className="rounded-circle d-flex align-items-center justify-content-center bg-success bg-opacity-10 text-success"
                style={{ width: "44px", height: "44px" }}
              >
                <FiUserCheck size={22} />
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-4">
          <div
            className={`card border-0 shadow-sm p-3 rounded-3 cursor-pointer ${
              activeTab === "managed" ? "border-start border-warning border-4" : ""
            }`}
            style={{ background: "#fffdf5", transition: "all 0.2s" }}
            onClick={() => setActiveTab("managed")}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <span className="text-muted small fw-medium">Managed as Admin</span>
                <h4 className="fw-bold text-warning mb-0 mt-1">{managedCommunities.length}</h4>
              </div>
              <div
                className="rounded-circle d-flex align-items-center justify-content-center bg-warning bg-opacity-10 text-warning"
                style={{ width: "44px", height: "44px" }}
              >
                <FiShield size={22} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs & Search Controls */}
      <div className="card border-0 shadow-sm rounded-3 mb-4">
        <div className="card-body p-3">
          <div className="row g-2 align-items-center">
            <div className="col-12 col-md-7">
              <ul className="nav nav-pills flex-nowrap overflow-auto pb-1 pb-md-0">
                <li className="nav-item">
                  <button
                    className={`nav-link btn-sm fw-medium px-3 me-2 text-nowrap ${
                      activeTab === "public" ? "active" : "text-secondary"
                    }`}
                    onClick={() => setActiveTab("public")}
                  >
                    Public Directory ({publicCommunities.length})
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link btn-sm fw-medium px-3 me-2 text-nowrap ${
                      activeTab === "joined" ? "active" : "text-secondary"
                    }`}
                    onClick={() => setActiveTab("joined")}
                  >
                    My Joined ({joinedCommunities.length})
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link btn-sm fw-medium px-3 text-nowrap ${
                      activeTab === "managed" ? "active" : "text-secondary"
                    }`}
                    onClick={() => setActiveTab("managed")}
                  >
                    Managed Communities ({managedCommunities.length})
                  </button>
                </li>
              </ul>
            </div>
            <div className="col-12 col-md-5">
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-white border-end-0">
                  <FiSearch className="text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Search community name or location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setSearchQuery("")}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Communities Grid */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading communities...</span>
          </div>
          <p className="text-muted mt-2 small">Loading neighborhood communities...</p>
        </div>
      ) : displayedCommunities.length === 0 ? (
        <div className="card border-0 shadow-sm rounded-3 py-5 text-center">
          <div className="card-body">
            <FiUsers size={48} className="text-muted mb-3 opacity-50" />
            <h5 className="fw-semibold text-secondary">No Communities Found</h5>
            <p className="text-muted small mb-3">
              {searchQuery
                ? `No results matching "${searchQuery}". Try a different keyword.`
                : activeTab === "managed"
                ? "You have not created any communities yet. Click 'Create Community' to begin."
                : activeTab === "joined"
                ? "You haven't joined any communities yet. Browse Public communities to join."
                : "No public communities are available at this time."}
            </p>
            {activeTab === "managed" && (
              <button
                className="btn btn-primary btn-sm"
                onClick={() => setShowCreateModal(true)}
              >
                Create Community
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="row g-3">
          {displayedCommunities.map((item) => {
            const isManaged = managedIds.has(String(item.id)) || item.role === "ADMIN";
            const isJoined = joinedIds.has(String(item.id)) || item.role === "MEMBER";
            const isBusy = actionLoadingId === item.id;

            return (
              <div key={item.id} className="col-12 col-md-6 col-lg-4">
                <div className="card h-100 border-0 shadow-sm rounded-3 overflow-hidden d-flex flex-column">
                  {/* Card Header Top Gradient */}
                  <div
                    className="p-3 text-white d-flex justify-content-between align-items-start"
                    style={{
                      background: isManaged
                        ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                        : isJoined
                        ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                        : "linear-gradient(135deg, #074da3 0%, #2563eb 100%)",
                    }}
                  >
                    <div>
                      <span className="badge bg-black bg-opacity-25 mb-1 text-uppercase" style={{ fontSize: "10px" }}>
                        {item.type || "PUBLIC"}
                      </span>
                      <h5 className="fw-bold mb-0 text-truncate" style={{ maxWidth: "220px" }} title={item.name}>
                        {item.name}
                      </h5>
                    </div>
                    {isManaged ? (
                      <span className="badge bg-white text-warning fw-bold px-2 py-1 shadow-sm">
                        Admin
                      </span>
                    ) : isJoined ? (
                      <span className="badge bg-white text-success fw-bold px-2 py-1 shadow-sm">
                        Joined
                      </span>
                    ) : null}
                  </div>

                  {/* Card Body */}
                  <div className="card-body p-3 d-flex flex-column flex-grow-1 justify-content-between">
                    <div>
                      <p className="text-muted small mb-2 text-truncate" title={item.description}>
                        <FiGlobe className="me-1 text-primary" />
                        {item.description || "Neighborhood Hub"}
                      </p>
                      {item.created_on && (
                        <p className="text-muted" style={{ fontSize: "11px" }}>
                          Created: {new Date(item.created_on).toLocaleDateString()}
                        </p>
                      )}
                    </div>

                    {/* Action buttons */}
                    <div className="pt-3 border-top mt-2">
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-outline-secondary btn-sm flex-grow-1 d-flex align-items-center justify-content-center gap-1"
                          onClick={() => handleOpenDetails(item)}
                        >
                          <FiInfo size={14} /> Members
                        </button>

                        {isManaged ? (
                          <button
                            className="btn btn-outline-danger btn-sm d-flex align-items-center justify-content-center gap-1 px-3"
                            onClick={() => handleDelete(item.id)}
                            disabled={isBusy}
                            title="Delete Community"
                          >
                            <FiTrash2 size={14} /> {isBusy ? "Deleting..." : "Delete"}
                          </button>
                        ) : isJoined ? (
                          <button
                            className="btn btn-outline-danger btn-sm d-flex align-items-center justify-content-center gap-1 px-3"
                            onClick={() => handleLeave(item.id)}
                            disabled={isBusy}
                            title="Leave Community"
                          >
                            <FiLogOut size={14} /> {isBusy ? "Leaving..." : "Leave"}
                          </button>
                        ) : (
                          <button
                            className="btn btn-primary btn-sm flex-grow-1 d-flex align-items-center justify-content-center gap-1"
                            onClick={() => handleJoin(item.id)}
                            disabled={isBusy}
                          >
                            <FiCheckCircle size={14} /> {isBusy ? "Joining..." : "Join"}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* =========================================================================
          CREATE COMMUNITY MODAL
          ========================================================================= */}
      {showCreateModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow rounded-3">
              <form onSubmit={handleCreateSubmit}>
                <div className="modal-header border-0 pb-0">
                  <h5 className="modal-title fw-bold text-dark d-flex align-items-center gap-2">
                    <FiUsers className="text-primary" /> Create Neighborhood Community
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowCreateModal(false)}
                    disabled={createSubmitting}
                  />
                </div>
                <div className="modal-body py-3">
                  {createError && (
                    <div className="alert alert-danger py-2 small mb-3">{createError}</div>
                  )}

                  <div className="mb-3">
                    <label className="form-label fw-semibold small text-dark">
                      Community Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Green Valley Residents Club"
                      value={newCommunityName}
                      onChange={(e) => setNewCommunityName(e.target.value)}
                      maxLength={100}
                      required
                    />
                    <div className="form-text small">
                      Choose a descriptive name that neighbors can easily recognize.
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold small text-dark">Community Type</label>
                    <div className="d-flex flex-column gap-2">
                      <label
                        className={`d-flex align-items-start gap-2 p-3 border rounded-3 cursor-pointer ${
                          newCommunityType === "PUBLIC" ? "border-primary bg-primary bg-opacity-10" : "bg-white"
                        }`}
                      >
                        <input
                          type="radio"
                          name="communityType"
                          value="PUBLIC"
                          className="form-check-input mt-1"
                          checked={newCommunityType === "PUBLIC"}
                          onChange={() => setNewCommunityType("PUBLIC")}
                        />
                        <div>
                          <div className="fw-semibold text-dark d-flex align-items-center gap-1">
                            <FiGlobe className="text-primary" /> Public Community
                          </div>
                          <div className="text-muted small">
                            Anyone in the neighborhood can discover and join directly.
                          </div>
                        </div>
                      </label>

                      <label
                        className={`d-flex align-items-start gap-2 p-3 border rounded-3 cursor-pointer ${
                          newCommunityType === "PRIVATE" ? "border-primary bg-primary bg-opacity-10" : "bg-white"
                        }`}
                      >
                        <input
                          type="radio"
                          name="communityType"
                          value="PRIVATE"
                          className="form-check-input mt-1"
                          checked={newCommunityType === "PRIVATE"}
                          onChange={() => setNewCommunityType("PRIVATE")}
                        />
                        <div>
                          <div className="fw-semibold text-dark d-flex align-items-center gap-1">
                            <FiLock className="text-warning" /> Private Community
                          </div>
                          <div className="text-muted small">
                            Restricted to invited members and explicit approval.
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="modal-footer border-0 pt-0">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setShowCreateModal(false)}
                    disabled={createSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm px-4"
                    disabled={createSubmitting}
                  >
                    {createSubmitting ? "Creating..." : "Create Community"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================================
          COMMUNITY DETAILS & MEMBERS MODAL
          ========================================================================= */}
      {showDetailsModal && selectedCommunity && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
            <div className="modal-content border-0 shadow rounded-3">
              <div className="modal-header bg-light border-0 py-3">
                <div>
                  <h5 className="modal-title fw-bold text-dark mb-0">
                    {selectedCommunity.name}
                  </h5>
                  <small className="text-muted">
                    {selectedCommunity.description || "Neighborhood Community"}
                  </small>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDetailsModal(false)}
                />
              </div>

              <div className="modal-body p-3">
                {detailsLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary spinner-border-sm" role="status" />
                    <p className="text-muted small mt-2">Loading members...</p>
                  </div>
                ) : (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="fw-bold mb-0 text-secondary">
                        Members Directory ({communityMembers.length})
                      </h6>
                      <span className="badge bg-primary bg-opacity-10 text-primary">
                        {selectedCommunity.type || "PUBLIC"}
                      </span>
                    </div>

                    {communityMembers.length === 0 ? (
                      <div className="text-center py-4 text-muted small">
                        No members found in this community yet.
                      </div>
                    ) : (
                      <div className="list-group list-group-flush border rounded-2">
                        {communityMembers.map((memberItem) => {
                          const user = memberItem.user || {};
                          const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim() || "Community Member";
                          const initials = getInitials(user.first_name, user.last_name);
                          const isAdminRole = memberItem.role === "ADMIN";
                          const isCurrentUserAdmin =
                            managedIds.has(String(selectedCommunity.id)) ||
                            selectedCommunity.role === "ADMIN";
                          const canRemove =
                            isCurrentUserAdmin &&
                            !isAdminRole &&
                            String(user.id) !== String(currentUserId);

                          return (
                            <div
                              key={memberItem.id}
                              className="list-group-item d-flex align-items-center justify-content-between p-3"
                            >
                              <div className="d-flex align-items-center gap-3">
                                <div
                                  className="rounded-circle bg-primary bg-opacity-10 text-primary fw-bold d-flex align-items-center justify-content-center"
                                  style={{ width: "42px", height: "42px", fontSize: "14px" }}
                                >
                                  {initials}
                                </div>
                                <div>
                                  <div className="fw-semibold text-dark">{fullName}</div>
                                  <div className="text-muted small">
                                    {user.email_address || user.mobile_number || "Verified Resident"}
                                  </div>
                                </div>
                              </div>

                              <div className="d-flex align-items-center gap-2">
                                <span
                                  className={`badge ${
                                    isAdminRole ? "bg-warning text-dark" : "bg-light text-secondary border"
                                  }`}
                                >
                                  {isAdminRole ? "Admin" : "Member"}
                                </span>

                                {canRemove && (
                                  <button
                                    className="btn btn-outline-danger btn-sm p-1 px-2"
                                    onClick={() => handleRemoveMember(user.id)}
                                    disabled={removingMemberId === user.id}
                                    title="Remove from community"
                                  >
                                    <FiUserX size={14} />
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="modal-footer border-0 pt-0">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowDetailsModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
